import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router';
import { DataTable, EmptyState, ErrorState, Field, Modal, Spinner, StatusBadge } from '../components/ui.jsx';
import { WelcomeBanner } from '../components/WelcomeBanner.jsx';
import { ApiError, api } from '../api/client.js';
import { useAuth } from '../state/AuthContext.jsx';
import { useMeta } from '../state/MetaContext.jsx';
import { useToast } from '../state/ToastContext.jsx';
import { useFetch, useTitle } from '../lib/hooks.js';
import { formatDate, formatXaf } from '../lib/format.js';
import { MoneySummary } from '../components/refunds.jsx';

const PAYOUT_LABELS = {
  mtn_momo: 'MTN Mobile Money',
  orange_money: 'Orange Money',
  bank_transfer: 'Bank transfer',
  other: 'Other'
};

const STATUS_BADGE = {
  active: { status: 'approved', label: 'On' },
  cut_off: { status: 'rejected', label: 'Cut off' },
  closed: { status: 'withdrawn', label: 'Closed' }
};

/**
 * Telling the landlord money has been sent.
 *
 * This is a claim, not a payment. Nothing moves on the customer's balance until
 * somebody on the landlord's side agrees it arrived, and the wording here says
 * so plainly so nobody thinks a bill is settled when it is not.
 */
const ReportPaymentModal = ({ item, bill, onSubmit, onClose }) => {
  const { paymentMethods } = useMeta();
  const [form, setForm] = useState({
    amount: String(bill.outstanding),
    method: item.payoutAccounts[0]?.kind === 'bank_transfer' ? 'bank_transfer' : 'mtn_momo',
    reference: '',
    paidOn: new Date().toISOString().slice(0, 10),
    note: ''
  });
  const [receipt, setReceipt] = useState(null);
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);

  const set = (patch) => setForm((current) => ({ ...current, ...patch }));

  // A preview so the customer can see they picked the right screenshot.
  const receiptPreview = useMemo(
    () => (receipt && receipt.type.startsWith('image/') ? URL.createObjectURL(receipt) : null),
    [receipt]
  );
  useEffect(() => () => receiptPreview && URL.revokeObjectURL(receiptPreview), [receiptPreview]);

  const submit = async (event) => {
    event.preventDefault();
    if (!receipt) {
      setErrors({ receipt: 'Attach a screenshot of the payment before sending.' });
      return;
    }
    setBusy(true);
    setErrors({});
    try {
      await onSubmit(
        {
          amount: Number(form.amount),
          method: form.method,
          reference: form.reference.trim() || null,
          paidOn: form.paidOn,
          note: form.note.trim() || null
        },
        receipt
      );
    } catch (error) {
      if (error instanceof ApiError && error.details) setErrors(error.details);
      else setErrors({ _: error.message });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      title={`Tell ${item.landlord.name} you have paid`}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn-ghost" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button
            type="submit"
            form="claim-form"
            className="btn btn-primary"
            disabled={busy || !receipt}
            title={receipt ? undefined : 'Attach a screenshot of the payment first'}
          >
            {busy ? 'Sending...' : 'Send it'}
          </button>
        </>
      }
    >
      {errors._ ? <div className="notice notice-bad">{errors._}</div> : null}

      <p className="modal-lead">
        Pay {item.landlord.name} however you normally do, then tell them here with a screenshot of the payment. The
        bill stays open until they confirm the money arrived - this does not pay it by itself.
      </p>

      {item.payoutAccounts.length > 0 ? (
        <div className="notice notice-info">
          <strong>Where to send it:</strong>
          <ul className="plain-list">
            {item.payoutAccounts.map((account) => (
              <li key={account.id}>
                {PAYOUT_LABELS[account.kind] || account.kind} - {account.accountNumber} ({account.accountName})
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      <form id="claim-form" className="stack" onSubmit={submit}>
        <div className="grid-2">
          <Field
            label="How much you sent"
            hint={`${formatXaf(bill.outstanding)} outstanding on ${bill.reference}.`}
            error={errors.amount}
            required
          >
            <input
              type="number"
              min="1"
              step="1"
              value={form.amount}
              onChange={(event) => set({ amount: event.target.value })}
              required
            />
          </Field>

          <Field label="How you sent it" error={errors.method}>
            <select value={form.method} onChange={(event) => set({ method: event.target.value })}>
              {paymentMethods.map((method) => (
                <option key={method.slug} value={method.slug}>
                  {method.label}
                </option>
              ))}
            </select>
          </Field>
        </div>

        <div className="grid-2">
          <Field
            label="Reference"
            hint="The MoMo transaction code, or the teller slip number."
            error={errors.reference}
          >
            <input type="text" value={form.reference} onChange={(event) => set({ reference: event.target.value })} />
          </Field>

          <Field label="When you sent it" error={errors.paidOn}>
            <input type="date" value={form.paidOn} onChange={(event) => set({ paidOn: event.target.value })} />
          </Field>
        </div>

        <Field label="Anything else they should know" error={errors.note}>
          <input type="text" value={form.note} onChange={(event) => set({ note: event.target.value })} />
        </Field>

        <Field
          label="Screenshot of the payment"
          hint="The MoMo confirmation message, or a photo of the bank slip. They check it before confirming."
          error={errors.receipt}
          required
        >
          <input
            type="file"
            accept="image/*,application/pdf"
            required
            onChange={(event) => setReceipt(event.target.files?.[0] || null)}
          />
        </Field>
        {receiptPreview ? (
          <img src={receiptPreview} alt="The screenshot you are attaching" className="receipt-preview" />
        ) : null}
      </form>
    </Modal>
  );
};

/**
 * What a utility customer sees. The same page serves a sitting tenant and a
 * neighbour on the landlord's meter, because to them it is the same thing: what
 * the dial said, what that came to, and by when.
 */
const MyUtilities = () => {
  useTitle('My utilities');
  const { isTenant } = useAuth();
  const toast = useToast();
  const [reporting, setReporting] = useState(null);
  const utilities = useFetch('/me/utilities');

  if (utilities.loading && !utilities.data) return <Spinner />;
  if (utilities.error) {
    return (
      <div className="container section">
        <ErrorState message={utilities.error} onRetry={utilities.reload} />
      </div>
    );
  }

  const { items } = utilities.data;

  const report = async (item, bill, payload, receipt) => {
    const formData = new FormData();
    for (const [key, value] of Object.entries(payload)) {
      if (value !== null && value !== undefined) formData.append(key, String(value));
    }
    formData.append('receipt', receipt);
    await api.upload(`/utility-bills/${bill.id}/claims`, formData);
    toast.success(`Sent. ${item.landlord.name} will confirm it and your receipt will follow.`);
    setReporting(null);
    utilities.reload();
  };

  const billColumns = (item, unitLabel) => [
    {
      key: 'reference',
      header: 'Bill',
      render: (bill) => (
        <div className="cell-stack">
          <span className="cell-title">{bill.reference}</span>
          <span className="muted">
            {bill.periodStart ? `${formatDate(bill.periodStart)} to ${formatDate(bill.periodEnd)}` : formatDate(bill.readOn)}
          </span>
          {bill.cancelReason ? <span className="overdue-flag">cancelled: {bill.cancelReason}</span> : null}
        </div>
      )
    },
    {
      key: 'reading',
      header: 'How it was worked out',
      render: (bill) =>
        bill.broughtForward ? (
          <div className="cell-stack">
            <span>Carried over from before</span>
            <span className="muted">{bill.broughtForward}</span>
          </div>
        ) : bill.billingMode === 'metered' ? (
          <div className="cell-stack">
            <span>
              {bill.currentReading} - {bill.previousReading} = {bill.unitsUsed} {unitLabel}
            </span>
            <span className="muted">
              {bill.unitsUsed} x {formatXaf(bill.unitCost)} = {formatXaf(bill.consumption)}
              {bill.standingCharge > 0 ? ` + ${formatXaf(bill.standingCharge)} standing charge` : ''}
            </span>
            {bill.rounding > 0 ? (
              <span className="muted">
                {formatXaf(bill.exact)}, rounded up to {formatXaf(bill.amount)}
              </span>
            ) : null}
            {bill.readingPhotoUrl ? (
              <a href={bill.readingPhotoUrl} target="_blank" rel="noreferrer" className="meter-photo meter-photo-sm">
                <img src={bill.readingPhotoUrl} alt={`Your meter on ${formatDate(bill.readOn)}`} />
              </a>
            ) : null}
          </div>
        ) : (
          <div className="cell-stack">
            <span>Fixed charge</span>
            <span className="muted">{formatXaf(bill.consumption)}</span>
            {bill.rounding > 0 ? (
              <span className="muted">
                {formatXaf(bill.exact)}, rounded up to {formatXaf(bill.amount)}
              </span>
            ) : null}
          </div>
        )
    },
    {
      key: 'amount',
      header: 'Amount',
      render: (bill) => (
        <div className="cell-stack">
          <strong>{formatXaf(bill.total)}</strong>
          {bill.lateFee > 0 ? (
            <span className="owing">
              {formatXaf(bill.amount)} + {formatXaf(bill.lateFee)} late fee
            </span>
          ) : null}
          {bill.lateFeeWaived ? <span className="muted">Late fee dropped by your landlord</span> : null}
          {bill.paid > 0 ? <span className="muted">{formatXaf(bill.paid)} confirmed</span> : null}
          {bill.outstanding > 0 ? <span className="owing">{formatXaf(bill.outstanding)} to pay</span> : null}
          {bill.claimed > 0 ? (
            <span className="claim-flag">{formatXaf(bill.claimed)} waiting to be confirmed</span>
          ) : null}
        </div>
      )
    },
    {
      key: 'due',
      header: 'Pay by',
      render: (bill) => (
        <div className="cell-stack">
          <span>{formatDate(bill.dueDate)}</span>
          {bill.overdue ? <span className="overdue-flag">cut off {formatDate(bill.cutOffDate)}</span> : null}
        </div>
      )
    },
    {
      key: 'actions',
      header: '',
      render: (bill) =>
        bill.outstanding > 0 ? (
          <button
            type="button"
            className="btn btn-primary btn-sm"
            onClick={() => setReporting({ item, bill })}
          >
            Pay this bill
          </button>
        ) : null
    }
  ];

  const receiptColumns = [
    {
      key: 'receipt',
      header: 'Receipt',
      render: (payment) => (
        <div className="cell-stack">
          <Link to={`/utility-receipts/${payment.id}`}>{payment.receiptNumber}</Link>
          {payment.voided ? <span className="void-flag">cancelled</span> : null}
        </div>
      )
    },
    { key: 'paidAt', header: 'Date', render: (payment) => formatDate(payment.paidAt) },
    { key: 'amount', header: 'Amount', render: (payment) => formatXaf(payment.amount) },
    {
      key: 'actions',
      header: '',
      render: (payment) => (
        <Link to={`/utility-receipts/${payment.id}`} className="btn btn-ghost btn-sm">
          View or print
        </Link>
      )
    }
  ];

  return (
    <div className="container section">
      <WelcomeBanner
        eyebrow="Your bills, explained"
        title="My utilities"
        text="Light, water and internet billed to you off your landlord's meter: what the dial said, what it came to, and by when."
      >
        {isTenant ? (
          <Link to="/tenant/rent" className="btn btn-outline">
            My rent
          </Link>
        ) : null}
      </WelcomeBanner>

      {items.length === 0 ? (
        <EmptyState
          title="Nothing billed to you"
          message="When your landlord puts you on a meter, your readings and bills appear here."
        />
      ) : null}

      {items.map((item) => (
        <section className="lease-panel" key={item.account.id}>
          <div className="lease-panel-head">
            <div>
              {/* A customer can hold two dials on one meter - the shop and the
                  room behind it - so the label is what tells their two cards
                  apart. */}
              <h2>
                {item.service.name}
                {item.account.label ? <span className="muted"> - {item.account.label}</span> : null}
              </h2>
              <p className="muted">
                {item.account.meterNumber ? `Meter ${item.account.meterNumber} - ` : ''}
                {item.landlord.name}
                {item.landlord.phone ? (
                  <>
                    {' - '}
                    <a href={`tel:${item.landlord.phone}`}>{item.landlord.phone}</a>
                  </>
                ) : null}
              </p>
            </div>
            <StatusBadge {...STATUS_BADGE[item.account.status]} />
          </div>

          <MoneySummary
            billed={item.totals.billed}
            paid={item.totals.received}
            owed={item.totals.owed}
            credit={item.totals.credit}
            refunded={item.totals.refunded}
            overdue={item.totals.overdue}
          />

          {item.account.status === 'cut_off' ? (
            <div className="notice notice-bad">
              <strong>This service has been cut off.</strong> Pay {formatXaf(item.totals.balance)} and it will be put
              back on.
            </div>
          ) : item.totals.overdue > 0 ? (
            <div className="notice notice-warn">
              <strong>{formatXaf(item.totals.overdue)} is overdue.</strong> The service is cut off two days after the
              deadline on the bill.
            </div>
          ) : item.totals.credit > 0 ? (
            <div className="notice notice-good">
              <strong>{formatXaf(item.totals.credit)} is in credit on this meter.</strong> You have paid more than you
              were billed. It counts against your next bill, or your landlord can refund it to you.
            </div>
          ) : item.totals.owed > 0 ? (
            <div className="notice notice-info">
              <strong>{formatXaf(item.totals.balance)} to pay.</strong>{' '}
              {item.totals.nextDue ? `Next deadline ${formatDate(item.totals.nextDue.dueDate)}.` : null}
            </div>
          ) : (
            <div className="notice notice-good">Nothing owing on this meter.</div>
          )}

          {item.totals.awaitingConfirmation > 0 ? (
            <div className="notice notice-info">
              <strong>{formatXaf(item.totals.awaitingConfirmation)} is waiting to be confirmed.</strong> You have told{' '}
              {item.landlord.name} about it. It does not come off what you owe until they confirm the money arrived.
            </div>
          ) : null}

          {item.payoutAccounts.length > 0 && item.totals.balance > 0 ? (
            <div className="notice notice-info">
              <strong>Where to pay:</strong>
              <ul className="plain-list">
                {item.payoutAccounts.map((account) => (
                  <li key={account.id}>
                    {PAYOUT_LABELS[account.kind] || account.kind} - {account.accountNumber} ({account.accountName})
                    {account.bankName ? `, ${account.bankName}` : ''}
                  </li>
                ))}
              </ul>
            </div>
          ) : null}

          <section className="section-block">
            <div className="section-head">
              <h3>Bills</h3>
              <span className="muted">Every reading taken and what it came to.</span>
            </div>
            <DataTable
              columns={billColumns(item, item.service.unitLabel)}
              rows={item.bills}
              empty={{ title: 'Nothing billed yet', message: 'Your first bill will appear here.' }}
            />
          </section>

          {item.mainReadings?.length ? (
            <section className="section-block">
              <div className="section-head">
                <h3>The main meter</h3>
                <span className="muted">
                  What the whole compound used, so you can see the total your share comes out of.
                </span>
              </div>
              <div className="main-reading-list">
                {item.mainReadings.map((reading) => (
                  <article className="main-reading-card" key={reading.id}>
                    <div className="cell-stack">
                      <strong>
                        {reading.unitsUsed} {item.service.unitLabel} for the whole compound
                      </strong>
                      <span className="muted">
                        {reading.previousReading} to {reading.currentReading}, read {formatDate(reading.readOn)}
                        {item.service.meterNumber ? ` on meter ${item.service.meterNumber}` : ''}
                      </span>
                      <span className="muted">
                        {formatDate(reading.periodStart)} to {formatDate(reading.periodEnd)}
                      </span>
                    </div>
                    {reading.photoUrl ? (
                      <a href={reading.photoUrl} target="_blank" rel="noreferrer" className="meter-photo">
                        <img src={reading.photoUrl} alt={`The main meter on ${formatDate(reading.readOn)}`} />
                      </a>
                    ) : null}
                  </article>
                ))}
              </div>
            </section>
          ) : null}

          <section className="section-block">
            <div className="section-head">
              <h3>My receipts</h3>
            </div>
            <DataTable
              columns={receiptColumns}
              rows={item.payments}
              empty={{ title: 'No receipts yet', message: 'A receipt appears here for every payment recorded.' }}
            />
          </section>
        </section>
      ))}

      {reporting ? (
        <ReportPaymentModal
          item={reporting.item}
          bill={reporting.bill}
          onSubmit={(payload, receipt) => report(reporting.item, reporting.bill, payload, receipt)}
          onClose={() => setReporting(null)}
        />
      ) : null}
    </div>
  );
};

export default MyUtilities;
